import { useAnimals, useDairyReport, useLots } from '@/api/hooks';
import { DairySubnav } from '@/components/dairy/DairySubnav';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { DateInput } from '@/components/ui/DateInput';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { formatLiters, milkingShiftLabel } from '@/lib/dairy';
import { formatDate, todayIso } from '@/lib/format';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

function startOfMonthIso(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function DairyReportPage() {
  const { propertyId } = usePropertyScope();

  const [from, setFrom] = useState(startOfMonthIso);
  const [to, setTo] = useState(todayIso);
  const [cowId, setCowId] = useState('');
  const [lotId, setLotId] = useState('');
  const [lactation, setLactation] = useState('');

  const filters = useMemo(
    () => ({
      property_id: propertyId ?? undefined,
      from,
      to,
      cow_id: cowId ? Number(cowId) : undefined,
      lot_id: lotId ? Number(lotId) : undefined,
      lactation: lactation ? Number(lactation) : undefined,
    }),
    [propertyId, from, to, cowId, lotId, lactation],
  );

  const { data: report, isLoading, isFetching, refetch } = useDairyReport(filters);
  const { data: animals } = useAnimals({
    property_id: propertyId ?? undefined,
    sex: 'female',
    per_page: 500,
  });
  const { data: lots } = useLots(propertyId);

  const cowOptions = (animals?.animals ?? []).map((a) => ({
    value: a.id,
    label: `${a.internal_number}${a.animal_name ? ` — ${a.animal_name}` : ''}`,
  }));
  const lotOptions = (lots?.lots ?? []).map((l) => ({ value: l.id, label: l.name }));

  return (
    <div>
      <PageHeader title="Relatório leiteiro" subtitle="Filtros por período, animal, lote e lactação" />
      <DairySubnav />

      {!propertyId ? (
        <Card>
          <CardBody className="py-10 text-center text-manejai-bark/60">Selecione uma propriedade.</CardBody>
        </Card>
      ) : (
        <>
          <Card className="mb-6">
            <CardBody className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <DateInput label="De" value={from} onChange={setFrom} />
              <DateInput label="Até" value={to} onChange={setTo} />
              <Select
                label="Animal"
                value={cowId}
                onChange={(e) => setCowId(e.target.value)}
                placeholder="Todos"
                options={cowOptions}
              />
              <Select
                label="Lote"
                value={lotId}
                onChange={(e) => setLotId(e.target.value)}
                placeholder="Todos"
                options={lotOptions}
              />
              <Select
                label="Lactação"
                value={lactation}
                onChange={(e) => setLactation(e.target.value)}
                placeholder="Todas"
                options={[1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: n, label: String(n) }))}
              />
              <div className="sm:col-span-2 lg:col-span-5">
                <Button type="button" variant="outline" onClick={() => refetch()} loading={isFetching}>
                  Atualizar
                </Button>
              </div>
            </CardBody>
          </Card>

          {isLoading ? (
            <div className="h-32 rounded-2xl bg-manejai-sand/40 animate-pulse" />
          ) : (
            <>
              <Card className="mb-6">
                <CardBody>
                  <p className="text-xs text-manejai-bark/60">Total no período</p>
                  <p className="font-display text-3xl font-semibold text-manejai-forest">
                    {formatLiters(report?.total_liters)}
                  </p>
                </CardBody>
              </Card>

              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <h2 className="font-display font-semibold text-manejai-forest">Produção diária</h2>
                  </CardHeader>
                  <CardBody className="p-0 max-h-80 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/55">
                          <th className="px-4 py-2">Data</th>
                          <th className="px-4 py-2">Litros</th>
                          <th className="px-4 py-2">Vacas</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-manejai-sand/50">
                        {(report?.daily ?? []).map((d) => (
                          <tr key={d.date}>
                            <td className="px-4 py-2">{formatDate(d.date)}</td>
                            <td className="px-4 py-2 font-medium">{formatLiters(d.total_liters)}</td>
                            <td className="px-4 py-2">{d.cows_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <h2 className="font-display font-semibold text-manejai-forest">Lançamentos</h2>
                  </CardHeader>
                  <CardBody className="p-0 max-h-80 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/55">
                          <th className="px-4 py-2">Data</th>
                          <th className="px-4 py-2">Animal</th>
                          <th className="px-4 py-2">Turno</th>
                          <th className="px-4 py-2">L</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-manejai-sand/50">
                        {(report?.entries ?? []).map((row) => (
                          <tr key={row.event_id}>
                            <td className="px-4 py-2">{formatDate(row.event_date)}</td>
                            <td className="px-4 py-2">
                              <Link to={`/animais/${row.cow_id}`} className="text-manejai-moss hover:underline">
                                {row.internal_number}
                              </Link>
                            </td>
                            <td className="px-4 py-2">{milkingShiftLabel(row.milking_shift)}</td>
                            <td className="px-4 py-2">{formatLiters(row.production_liters)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardBody>
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
